import React from 'react';
import Box from '@mui/material/Box';
import { useListContext } from '../../ListContext';
import Pagination from '../../Pagination/Pagination';
import GridHeader from './GridHeader';
import RowGrid from './RowGrid';
import Alert from '../../../ui/mui-builder/Alert/Alert';
import { UiThemeOverrider, useUiTheme } from 'framework/uiTheme';
import ListFieldLayout from '../../../ui/layout/ListFieldLayout/ListFieldLayout';
import TotalRecords from '../TotalRecords';
import ListLoadingDecorator from '../ListLoadingDecorator';

interface GridLayoutProps {
  children: React.ReactElement[];
  rowComponent?: React.ComponentType<any>;
  rowProps?: any;
  rowKeySource?: string;
  buttons?: React.ReactElement[];
  showTotal?: boolean;
  showPagination?: boolean;
  isShowHeader?: boolean;
  customEmptyMessage?: string;
  expandable?: boolean;
  isExpandableItem?: (data: any) => boolean;
  buttonContainerSize?: number;
  expandableSection?: React.ReactNode;
  hasBackgroundLoader?: boolean;
  hasPositionFixedLoader?: boolean;
  isInitialLoaderPosition?: boolean;
  minWidth?: string;
  itemContainerSx?: Record<string, any>;
  expandIconInFirstCell?: boolean;
  useCollapseForExpandable?: boolean;
  renderExpandableSectionInsideBox?: boolean;
}

const DataGridLayoutGrid = ({
  children,
  rowComponent: RowComponent = RowGrid,
  rowProps = {},
  rowKeySource = 'id',
  buttons = [],
  hasBackgroundLoader = false,
  hasPositionFixedLoader = false,
  isInitialLoaderPosition = false,
  showPagination = true,
  showTotal = true,
  isShowHeader = true,
  customEmptyMessage = 'No records found',
  expandable = false,
  isExpandableItem,
  buttonContainerSize = 1,
  expandableSection,
  itemContainerSx,
  expandIconInFirstCell,
  useCollapseForExpandable,
  renderExpandableSectionInsideBox,
  minWidth = '1000px',
}: GridLayoutProps) => {
  const { data: { items = [], totalElements } = {}, loading } = useListContext();
  const { Alert: ThemedAlert = Alert } = useUiTheme();

  const hasItems = items.length > 0;

  const renderEmpty = () => (
    <ThemedAlert color="info" sx={{ mt: 1.5 }}>
      {customEmptyMessage}
    </ThemedAlert>
  );

  const renderRows = () =>
    items.map((item: any, index: number) => (
      <RowComponent
        key={`row-${item?.[rowKeySource] ?? index}`}
        data={item}
        template={children}
        buttons={buttons}
        rowProps={rowProps}
        rowKeySource={rowKeySource}
        expandable={expandable}
        isExpandableItem={isExpandableItem}
        buttonContainerSize={buttonContainerSize}
        expandableSection={expandableSection}
        containerSx={itemContainerSx}
        expandIconInFirstCell={expandIconInFirstCell}
        useCollapseForExpandable={useCollapseForExpandable}
        renderExpandableSectionInsideBox={renderExpandableSectionInsideBox}
      />
    ));

  const renderFooter = () => {
    if (!showPagination || !totalElements) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          mt: 2,
          gap: 2,
        }}
      >
        <Pagination />
        {showTotal && <TotalRecords />}
      </Box>
    );
  };

  return (
    <ListLoadingDecorator
      items={items}
      loading={loading}
      hasBackground={hasBackgroundLoader}
      hasPositionFixed={hasPositionFixedLoader}
      isInitialPosition={isInitialLoaderPosition}
    >
      <UiThemeOverrider overriddenLayouts={{ BaseFieldLayout: ListFieldLayout }}>
        <Box overflow="auto">
          <Box minWidth={minWidth}>
            <GridHeader
              expandable={expandable}
              buttons={buttons}
              isShowHeader={isShowHeader}
              buttonContainerSize={buttonContainerSize}
              expandIconInFirstCell={expandIconInFirstCell}
            >
              {children}
            </GridHeader>

            {hasItems ? renderRows() : renderEmpty()}
          </Box>
        </Box>
        {renderFooter()}
      </UiThemeOverrider>
    </ListLoadingDecorator>
  );
};

export default DataGridLayoutGrid;
